package fr.frux.feature.map

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.HelpOutline
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.format.formatDistance
import fr.frux.core.designsystem.icons.rememberSpeciesGlyphPainters
import fr.frux.core.designsystem.layout.LocalRootChromeInsets
import fr.frux.core.designsystem.theme.FruxStatus
import fr.frux.core.maps.CameraMoveSource
import fr.frux.core.maps.FruxMap
import fr.frux.core.maps.Location
import fr.frux.core.maps.MapCameraPosition
import fr.frux.core.maps.MapCameraState
import fr.frux.core.maps.MarkerId
import fr.frux.core.maps.aimPinOffset
import fr.frux.core.maps.mapAttributionCoversOpenStreetMap
import fr.frux.core.maps.rememberMapCameraState
import fr.frux.core.maps.visibleBounds
import fr.frux.core.maps.visibleRadiusMeters
import fr.frux.feature.map.aim.AIM_DUPLICATE_DEBOUNCE_MS
import fr.frux.feature.map.aim.AimLayers
import fr.frux.feature.map.aim.MapAimState
import fr.frux.feature.map.aim.allowsCameraTracking
import fr.frux.feature.map.aim.allowsViewModelCameraEvents
import fr.frux.feature.map.aim.markerSelection
import fr.frux.feature.map.aim.markerTrees
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_marker_callout_needs_check
import fr.frux.feature.map.resources.map_marker_callout_with_distance
import fr.frux.feature.map.site.SITE_DETAIL_ZOOM
import fr.frux.feature.map.site.SiteLayerViewModel
import fr.frux.feature.map.site.clearSelectionWhenTreeSelected
import fr.frux.feature.map.site.rememberSiteLayerViewModel
import fr.frux.feature.map.site.sitesAndPins
import fr.frux.feature.map.site.toMapPlaques
import fr.frux.shared.domain.model.Site
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.distanceMetersFrom
import fr.frux.shared.domain.model.needsCheck
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.map
import org.jetbrains.compose.resources.stringResource
import kotlin.time.ExperimentalTime

/** Zoom d'approche sur un arbre designe, ou sur un cadre trop petit pour etre cadre. */
internal const val SELECTED_TREE_ZOOM = 15f

/**
 * Repos de camera avant d'ecrire la vue memorisee. Plus long que [CAMERA_DEBOUNCE_MS] : armer une
 * pastille doit suivre le geste de pres, ecrire sur le disque non.
 */
internal const val MAP_VIEW_SAVE_DEBOUNCE_MS = 1_000L

/**
 * Apercu de dev de la pyramide de regroupements (#622) : pyramide seule sous le zoom 12, lieux
 * masques. Reste faux dans le depot, retire au lot 3 qui fera la vraie bascule de regime. Faux, la
 * pyramide n'est pas meme traduite pour la carte, voir [rememberPyramidPreview].
 */
internal const val PYRAMID_PREVIEW = false

/**
 * Etat de la surface carte persistante : le ViewModel et la camera vivent au niveau de
 * l'application (App), pas d'un ecran de la pile de navigation.
 *
 * C'est la decision qui ferme #124 : Nav3 ne compose que l'entree au sommet de la pile, donc tout
 * etat porte par l'entree `Route.Map` etait detruit au moindre changement d'onglet ou push. La
 * carte etant la surface principale du produit, son etat — camera, arbres charges, selection —
 * appartient a l'application entiere.
 */
@Stable
class MapHostState internal constructor(
    val viewModel: MapViewModel,
    val camera: MapCameraState,
    /** Couche des lieux de cueillette, alimentee par les chargements d'arbres de [viewModel]. */
    val sites: SiteLayerViewModel,
    val aim: MapAimState = MapAimState(camera),
) {
    /**
     * Compteur incremente a chaque geste utilisateur sur la carte, tap ou pan (#163). MapScreen
     * l'observe pour fermer le clavier : la surface carte n'est pas un descendant de l'ecran
     * (ADR 0009), elle ne peut donc pas appeler `LocalFocusManager` elle-meme. Un compteur plutot
     * qu'un evenement one-shot en `Channel` : deux gestes rapproches (pan puis tap) ne doivent pas
     * se perdre si l'ecran n'a pas encore recompose entre les deux, et `mutableIntState` garde
     * simplement la derniere valeur observee.
     */
    var mapGestureTick: Int by mutableIntStateOf(0)
        private set

    internal fun notifyMapGesture() {
        mapGestureTick++
    }

    /**
     * Ramene la surface persistante a son etat de depart : elle survit au changement
     * d'utilisateur, donc rien d'autre ne la nettoie a la deconnexion (#48, ADR 0009).
     *
     * La camera fait partie de la purge : une carte laissee cadree sur le quartier du precedent
     * utilisateur est une fuite d'information sur un appareil partage. Sans animation — ce n'est
     * pas un deplacement a suivre du regard, c'est un ecran qui change de main.
     */
    fun reset() {
        viewModel.reset()
        sites.reset()
        aim.stop()
        camera.moveTo(DEFAULT_CAMERA, animate = false)
    }
}

/**
 * Cree l'etat hote de la carte. Le ViewModel est rattache au ViewModelStore de l'hote de
 * l'application (pas a une entree de navigation) : il survit a toute la session.
 */
@Composable
fun rememberMapHostState(): MapHostState {
    val sites = rememberSiteLayerViewModel()
    val viewModel = rememberMapViewModel(sites::onAreaRequested)
    val camera = rememberMapCameraState(DEFAULT_CAMERA)
    return remember(viewModel, camera, sites) { MapHostState(viewModel, camera, sites) }
}

/**
 * Montre un lieu tout juste cree ou designe depuis un autre ecran : selectionne sur la carte a la
 * place de tout arbre, visee arretee, camera dessus. Extension du module parce que le zoom
 * d'approche lui est interne.
 */
fun MapHostState.focusOnSite(site: Site) {
    // Une seule selection a la fois, et la visee ne survit pas a un recadrage sur un autre sujet.
    aim.stop()
    clearTreeSelection()
    sites.announceCreated(site)
    camera.moveTo(MapCameraPosition(Location(site.latitude, site.longitude), zoom = SELECTED_TREE_ZOOM))
}

/**
 * La vue carte elle-meme, composee en permanence par App **sous** le NavDisplay et jamais
 * demontee : les autres ecrans, opaques, la masquent sans la detruire. MapLibre rend a la demande
 * — carte cachee et immobile, zero frame — donc le cout de cette permanence est la memoire
 * retenue (style, tuiles decodees, buffers GL), pas du GPU.
 *
 * Tout ce qui doit continuer de tourner quand l'ecran carte n'est pas au sommet vit ici : le
 * relais gestes → ViewModel et la consommation des evenements camera. [MapScreen] ne garde que le
 * chrome (recherche, filtres, fiches), sur fond transparent.
 *
 * @param interactive faux des que la carte ne recoit pas les gestes (spec §3 et §4, cf.
 *   [fr.frux.navigation.isMapInteractive]) : un scrim consomme alors tous les pointeurs en passe
 *   Initial. Indispensable — le fond opaque des autres ecrans peint mais ne consomme pas, et sans
 *   scrim un geste sur l'onglet Profil ferait paner la carte invisible dessous (l'interop Android
 *   ne s'arrete que sur une consommation en passe Initial).
 * @param onMarkerOpen non nul quand la carte est vivante a cote d'une colonne grand ecran et que
 *   l'ecran carte n'est pas compose : un marqueur tape ouvre directement sa fiche, aucune
 *   selection n'est ecrite dans le ViewModel, un groupe est ignore.
 * @param onPlaqueOpen pendant de [onMarkerOpen] pour les plaques de lieu.
 */
@OptIn(FlowPreview::class)
@Composable
fun MapSurface(
    host: MapHostState,
    interactive: Boolean,
    modifier: Modifier = Modifier,
    onMarkerOpen: ((Long) -> Unit)? = null,
    onPlaqueOpen: ((Long) -> Unit)? = null,
) {
    val viewModel = host.viewModel
    val camera = host.camera
    val state by viewModel.state.collectAsState()
    val siteState by host.sites.state.collectAsState()
    // Lu a travers derivedStateOf : la position change a chaque frame de geste, le seuil rarement.
    val detailZoom by remember(camera) { derivedStateOf { camera.position.zoom >= SITE_DETAIL_ZOOM } }
    val preview = rememberPyramidPreview(viewModel, camera, state)
    val aimActive = host.aim.isActive
    // Un arbre choisi hors de la carte (liste, recherche, lien) referme la fiche de lieu ouverte.
    LaunchedEffect(host) {
        host.sites.clearSelectionWhenTreeSelected(snapshotFlow { state.selectedTreeId })
    }

    // Seule composable toujours vivante (ADR 0009) a savoir si la carte est reellement a l'ecran
    // (#515) : `MapScreen` n'est pas recompose quand un panneau `MapZone` est au sommet de la pile
    // en grand ecran, alors que `interactive` (deja recu de l'hote) reste vrai dans ce cas.
    LaunchedEffect(interactive) { viewModel.onMapVisibilityChanged(interactive) }

    // Colonne grand écran (spec §5) : l'attribution des tuiles ne passe jamais dessous. Relayé par
    // un composable feuille dédié et non lu ici : LocalRootChromeInsets est dynamique (I2), lire
    // le local directement dans le corps de MapSurface recomposerait toute la surface carte à
    // chaque changement de dégagement.
    ChromeInsetsRelay(camera)

    // Taille reelle de la surface carte, en dp. Le rayon visible s'en deduit : sans elle, on ne
    // sait pas quelle etendue un niveau de zoom couvre.
    var viewportSize by remember { mutableStateOf(DpSize.Zero) }
    val density = LocalDensity.current

    // Extrait en fonction dediee (detekt LongMethod) : voir sa KDoc pour le detail du filtre GESTURE.
    ObserveMapGestures(host, viewModel, camera, viewportSize)
    AimSideEffects(host)

    ObserveViewModelCameraEvents(host)
    FollowUserLocationEffect(viewModel)

    RestoreAndPersistView(host)

    Box(
        modifier =
            modifier
                .fillMaxSize()
                .onSizeChanged { size ->
                    viewportSize = with(density) { DpSize(size.width.toDp(), size.height.toDp()) }
                }
                // Pendant du scrim, cote semantique (#146) : masquee, la surface publiait encore ses
                // marqueurs et son attribution dans l'arbre d'accessibilite, sous le contenu de
                // l'onglet courant. Le curseur virtuel d'un lecteur d'ecran les atteignait — le
                // tabIndex = -1 ne protege que le parcours clavier.
                //
                // `clearAndSetSemantics` et non `hideFromAccessibility` : le second pose un drapeau que
                // chaque plateforme doit honorer a la lecture de l'arbre, et la cible web ne le fait pas
                // — verifie a l'ecran, les marqueurs restaient publies. Le premier coupe le sous-arbre
                // a sa construction, donc partout.
                .then(if (interactive) Modifier else Modifier.clearAndSetSemantics {}),
    ) {
        // L'epingle selectionnee (ou le doublon candidat en visee) reste visible sous la plaque de son lieu.
        val markerSelection = host.aim.markerSelection(state.selectedTreeId)
        val nowMillis = state.now.toEpochMilliseconds()
        val lockedSiteIds = remember(siteState.sites, nowMillis) { siteState.lockedSiteIds(nowMillis) }
        val (shownSites, pinnedTrees) = state.sitesAndPins(siteState.sites, detailZoom, markerSelection)
        FruxMap(
            camera = camera,
            markers =
                host.aim.markerTrees(pinnedTrees).toMapMarkers(
                    markerSelection,
                    state.speciesHarvests,
                    state.currentMonth,
                    state.now,
                    lockedSiteIds,
                ),
            userLocation = state.userLocation,
            plaques =
                shownSites.takeUnless { preview.shown }.orEmpty().toMapPlaques(
                    selectedId = if (aimActive) null else siteState.selectedSiteId,
                    trees = state.visibleTrees,
                    statusOf = state::resolvedStatusOf,
                    nowMillis = nowMillis,
                    detailZoom = detailZoom,
                ),
            onPlaqueClick = { plaqueId -> onPlaqueTapped(host, onPlaqueOpen, plaqueId.value) },
            markerIcons = rememberSpeciesGlyphPainters(),
            onMarkerClick = { markerId -> onMarkerTapped(host, onMarkerOpen, markerId) },
            onMarkerGroupClick = { markerIds -> onMarkerGroupTapped(host, onMarkerOpen, markerIds) },
            // Attribution des donnees d'arbres (#501, cinq sources) dans le panneau du bouton (i),
            // a la suite du credit des tuiles. Seul le segment OSM depend des tuiles : quand
            // elles creditent deja OpenStreetMap (Protomaps), le repeter ferait lire deux fois le
            // meme credit cote a cote.
            attributions = dataAttributions(includeOsm = !mapAttributionCoversOpenStreetMap()),
            markerCallout = if (aimActive) null else markerCalloutFor(state),
            onUserGesture = host::notifyMapGesture, // Tap carte, meme signal que le pan ci-dessus.
            pyramid = preview.pyramid,
            pyramidOpacity = if (preview.shown) 1f else 0f,
            onPyramidTreeClick = { markerId -> onMarkerTapped(host, onMarkerOpen, markerId) },
            modifier = Modifier.fillMaxSize(),
        )

        AimLayers(host)

        if (!interactive) {
            Box(
                modifier =
                    Modifier
                        .fillMaxSize()
                        .pointerInput(Unit) {
                            awaitPointerEventScope {
                                while (true) {
                                    awaitPointerEvent(PointerEventPass.Initial)
                                        .changes
                                        .forEach { it.consume() }
                                }
                            }
                        },
            )
        }
    }
}

/**
 * Restauration de la vue memorisee puis persistance des mouvements, sortie de [MapSurface] (detekt
 * `LongMethod`).
 */
@OptIn(FlowPreview::class)
@Composable
private fun RestoreAndPersistView(host: MapHostState) {
    val viewModel = host.viewModel
    val camera = host.camera
    // Restauration puis persistance, dans cet ordre et dans le meme effet — c'est ce qui evite
    // la course : la camera demarre sur le repli, et un collecteur d'ecriture lance en parallele
    // ecraserait la vue memorisee avant meme qu'on ait fini de la lire.
    LaunchedEffect(viewModel) {
        val restored = viewModel.restoredView()
        if (restored != null) {
            // Sans animation : ce n'est pas un deplacement a suivre du regard, c'est l'ecran qui
            // s'ouvre la ou l'utilisateur l'avait laisse.
            camera.moveTo(restored, animate = false)
        }
        viewModel.onInitialView(restored ?: DEFAULT_CAMERA, restored = restored != null)

        snapshotFlow { camera.lastMove }
            // L'origine du mouvement se lit a l'emission, avant le debounce : un mouvement ne en
            // visee ne doit pas etre sauve apres stop().
            .map { it to host.aim.allowsCameraTracking() }
            .debounce(MAP_VIEW_SAVE_DEBOUNCE_MS)
            // Toutes les sources, pas seulement les gestes : une fois qu'un geste reel a arme la
            // persistance (viewModel.saveView filtre lui-meme sur persistenceArmed, arme par le
            // meme filtre GESTURE que ci-dessus), un recadrage programmatique ulterieur — apres
            // recherche par exemple — merite d'etre memorise autant qu'un pan. Ce qui compte est ce
            // que l'utilisateur a sous les yeux, pas comment il y est arrive. Mais tant qu'aucun
            // geste n'a eu lieu, rien ne s'ecrit : c'est ce qui protege la vue restauree du cadrage
            // automatique du tout premier chargement.
            // Sauf en visee : la camera y sert l'assistant, pas la vue que la personne retrouvera.
            .collect { (move, tracked) -> if (tracked) viewModel.saveView(move.position) }
    }
}

/**
 * Evenements camera one-shot du ViewModel. Consommes ici et non dans MapScreen : ce composable est
 * le seul toujours vivant : un recentrage demande depuis l'ecran d'ajout (focusOnNewTree) part
 * avant que MapScreen ne soit recompose. Ignores pendant la visee, ou ils recadreraient la carte
 * sous l'epingle.
 */
@Composable
private fun ObserveViewModelCameraEvents(host: MapHostState) {
    val camera = host.camera
    LaunchedEffect(host.viewModel) {
        host.viewModel.events.collect { event ->
            if (!host.aim.allowsViewModelCameraEvents()) return@collect
            when (event) {
                is MapEvent.CenterOn -> {
                    camera.moveTo(MapCameraPosition(event.target, zoom = event.zoom ?: SELECTED_TREE_ZOOM))
                }

                is MapEvent.FrameOn -> {
                    camera.fitBounds(event.bounds)
                }
            }
        }
    }
}

/**
 * Seul ce nœud recompose quand le dégagement racine change, pas la surface carte entière. Un
 * `SideEffect` et non un `LaunchedEffect` : la valeur doit suivre chaque recomposition du
 * squelette, sans coroutine.
 */
@Composable
private fun ChromeInsetsRelay(camera: MapCameraState) {
    val insets = LocalRootChromeInsets.current
    SideEffect { camera.chromeInsets = insets }
}

/** Meme statut resolu que l'epingle de l'arbre : la plaque d'un lieu se lit comme ses arbres. */
@OptIn(ExperimentalTime::class)
private fun MapState.resolvedStatusOf(tree: Tree): FruxStatus =
    tree.resolvedStatus(speciesHarvests[tree.species.id].orEmpty(), currentMonth, now)

private fun markerCalloutFor(state: MapState): @Composable (MarkerId) -> Unit =
    { markerId -> MapMarkerCallout(markerId, state) }

/**
 * Effets propres a la visee : le candidat de doublon se recalcule au repos de la camera, sur les
 * arbres deja charges, et le decalage de l'epingle suit le chrome. Isole de [MapSurface] pour que
 * ces lectures ne la recomposent pas.
 */
@OptIn(FlowPreview::class)
@Composable
private fun AimSideEffects(host: MapHostState) {
    val aim = host.aim
    val camera = host.camera
    val layoutDirection = LocalLayoutDirection.current
    // Lu via un State pour que snapshotFlow suive aussi l'arrivee de nouveaux arbres.
    val state by host.viewModel.state.collectAsState()
    // Lu en composition (pas dans le SideEffect) pour que le decalage suive tout changement de chrome.
    val chromeInsets = camera.chromeInsets
    SideEffect { aim.pinOffset = aimPinOffset(chromeInsets, layoutDirection) }
    // La selection d'Explorer ne doit pas reapparaitre au retour de la visee.
    LaunchedEffect(aim.isActive) {
        if (aim.isActive) {
            host.viewModel.clearSelection()
            host.sites.clearSelection()
        }
    }
    // « Me localiser » en visee : la position centre l'epingle, sans le cadrage d'Explorer.
    // Le drapeau ne tombe qu'une fois la mesure finie : avant, userLocation est l'ancienne position.
    LaunchedEffect(aim.isActive, aim.locateRequested, state.locateSequence, state.userLocation) {
        aim.consumeLocate(state.locateSequence, state.userLocation)?.let(aim::centerOn)
    }
    LaunchedEffect(aim.isActive) {
        if (!aim.isActive) return@LaunchedEffect
        snapshotFlow { camera.position to state.visibleTrees }
            .debounce(AIM_DUPLICATE_DEBOUNCE_MS)
            .collect { (_, trees) -> aim.onAimSettled(trees) }
    }
}

/**
 * Tap sur un marqueur. A cote d'une colonne grand ecran ([onMarkerOpen] non nul), la fiche
 * s'ouvre directement et la selection precedente (faite sur Explorer) est effacee pour ne pas
 * rester en bulle a cote d'une fiche qui parle d'un autre arbre. Sinon, selection classique.
 */
private fun onMarkerTapped(
    host: MapHostState,
    onMarkerOpen: ((Long) -> Unit)?,
    markerId: MarkerId,
) {
    // Sans effet en visee : en grand ecran un tap ouvrirait une fiche a la place de l'assistant.
    if (host.aim.isActive) return
    val viewModel = host.viewModel
    // Un arbre et un lieu ne sont jamais selectionnes ensemble : une seule fiche a la fois.
    host.sites.clearSelection()
    if (onMarkerOpen != null) {
        viewModel.clearSelection()
        onMarkerOpen(markerId.value)
    } else {
        viewModel.dismissMarkerGroup()
        viewModel.toggleTree(markerId.value, SelectionOrigin.MAP)
    }
}

/**
 * Tap sur une plaque de lieu, meme logique que [onMarkerTapped] : fiche directe a cote d'une colonne
 * grand ecran, sinon selection exclusive avec celle des arbres.
 */
internal fun onPlaqueTapped(
    host: MapHostState,
    onPlaqueOpen: ((Long) -> Unit)?,
    siteId: Long,
) {
    if (host.aim.isActive) return
    host.clearTreeSelection()
    if (onPlaqueOpen != null) {
        host.sites.clearSelection()
        onPlaqueOpen(siteId)
    } else {
        host.sites.select(siteId)
    }
}

/**
 * Un lieu remplace toute selection cote arbres, feuille de groupe comprise : elle passe avant la
 * carte de resume du lieu dans l'emplacement du bas, qui ne s'afficherait jamais.
 */
private fun MapHostState.clearTreeSelection() {
    viewModel.clearSelection()
    viewModel.dismissMarkerGroup()
}

/**
 * Tap sur un groupe de marqueurs. A cote d'une colonne grand ecran, l'ecran carte est absent et
 * la feuille de groupe n'a nulle part ou s'afficher : le geste est ignore, plutot que de surgir
 * plus tard, en surprise, au retour sur Explorer.
 */
private fun onMarkerGroupTapped(
    host: MapHostState,
    onMarkerOpen: ((Long) -> Unit)?,
    markerIds: List<MarkerId>,
) {
    if (onMarkerOpen != null || host.aim.isActive) return
    val viewModel = host.viewModel
    host.sites.clearSelection()
    val distinctIds = markerIds.distinct()
    when (distinctIds.size) {
        0 -> Unit
        1 -> viewModel.toggleTree(distinctIds.single().value, SelectionOrigin.MAP)
        else -> viewModel.onMarkerGroupSelected(distinctIds.map { it.value })
    }
}

/**
 * La camera est un etat Compose ecrit a chaque frame de geste : `snapshotFlow` le transforme en
 * flux, que le ViewModel debounce et compare au lot charge. Seuls les gestes remontent : un
 * deplacement demande par l'application (recentrage, cadrage) n'est pas une intention de
 * l'utilisateur, et le relire comme telle armerait la pastille sur notre propre ordre.
 *
 * Le rayon visible est calcule ici et non dans le ViewModel : c'est le seul endroit qui connaisse
 * la taille de la surface, et le ViewModel n'a pas a manipuler de pixels.
 *
 * Ce filtre GESTURE est partage par deux effets : il decide de la pastille « Rechercher dans cette
 * zone » ici, et dans [MapSurface] il est aussi le seul armement de la persistance de la vue
 * (`persistenceArmed`) — l'elargir remonterait des deplacements programmatiques comme des gestes,
 * et casserait silencieusement la memorisation, sans qu'aucun test ne le voie (les tests de
 * ViewModel appellent `onCameraMoved` directement, jamais via ce filtre).
 *
 * [MapHostState.notifyMapGesture] est aussi appele ici (#163) : un pan est un geste sur la carte au
 * meme titre qu'un tap, et [MapScreen] s'en sert pour fermer le clavier dans les deux cas.
 */
@Composable
private fun ObserveMapGestures(
    host: MapHostState,
    viewModel: MapViewModel,
    camera: MapCameraState,
    viewportSize: DpSize,
) {
    LaunchedEffect(camera, viewportSize) {
        snapshotFlow { camera.lastMove }
            .filter { it.source == CameraMoveSource.GESTURE }
            .collect { move ->
                host.notifyMapGesture()
                // La visee deplace la carte sans chercher d'arbres : aucune requete par geste.
                if (!host.aim.allowsCameraTracking()) return@collect
                viewModel.onCameraMoved(
                    center = move.position.target,
                    visibleRadiusMeters = visibleRadiusMeters(move.position, viewportSize),
                    visibleBounds = visibleBounds(move.position, viewportSize),
                )
            }
    }
}

/**
 * Contenu de la bulle ancree au-dessus du marqueur selectionne (#196 lot 5). Pas d'action au tap
 * pour l'instant : `MapSurface`/`MapHost` n'a pas acces a la navigation (ADR 0009, la surface
 * carte est composee sous le NavDisplay, independamment de l'ecran) — seul `MapScreen` connait
 * `onTreeClick`. Point ouvert, pas resolu ici volontairement.
 */
@Composable
private fun MapMarkerCallout(
    markerId: MarkerId,
    state: MapState,
) {
    val tree = state.visibleTrees.find { it.id == markerId.value } ?: return
    val distance = tree.distanceMetersFrom(state.userLocation ?: state.queryCenter)
    val text =
        if (distance != null) {
            stringResource(
                Res.string.map_marker_callout_with_distance,
                tree.species.commonNameFr,
                formatDistance(distance),
            )
        } else {
            tree.species.commonNameFr
        }
    Surface(
        shape = RoundedCornerShape(9.dp),
        // `inverseSurface` (encre brune en clair, creme en sombre) plutot que le vert fonce de la
        // maquette : ce vert n'est pas un token de theme, le figer donnerait une bulle sombre sur
        // fond sombre. La bulle suit le theme, la maquette donne l'intention.
        color = MaterialTheme.colorScheme.inverseSurface,
        shadowElevation = 3.dp,
    ) {
        Column(modifier = Modifier.padding(horizontal = 11.dp, vertical = 5.dp)) {
            Text(
                text = text,
                style = MaterialTheme.typography.labelMedium,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.inverseOnSurface,
            )
            // Ce que le contour crenele ne peut pas dire (#265) : la bulle est le premier endroit
            // ou l'utilisateur apprend ce que la denture signifie. Ton neutre, jamais une alerte —
            // l'ADR 0015 reserve le rouge au sosie mortel.
            if (tree.needsCheck) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    Icon(
                        imageVector = Icons.Outlined.HelpOutline,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.inverseOnSurface.copy(alpha = 0.7f),
                        modifier = Modifier.size(13.dp),
                    )
                    Text(
                        text = stringResource(Res.string.map_marker_callout_needs_check),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.inverseOnSurface.copy(alpha = 0.7f),
                    )
                }
            }
        }
    }
}
